import styled from '@emotion/native';
import { GroupSecurity } from '@quromedical/fhir-common';
import {
  type DefaultNavigatorOptions,
  type ParamListBase,
  type TabNavigationState,
  TabRouter,
  type TabRouterOptions,
} from '@react-navigation/native';
import { Row } from 'components/base';
import { type IconName, Visible } from 'design-system/base';
import { Logo, Profile } from 'design-system/navigation';
import { BASE_SIZE, PADDING } from 'design-system/theme';
import { Navigator, router, Slot } from 'expo-router';
import { useUserSession } from 'hooks';
import * as React from 'react';
import { useWindowDimensions } from 'react-native';

import { Tab } from './tab';

interface TabAction {
  icon: IconName;
  title: string;
  isHidden?: boolean;
  onPress: () => void;
}

// Props accepted by the view
type TabNavigationConfig = {
  name?: string;
  initials?: string;
  actions?: TabAction[];
};

// Supported screen options
type TabNavigationOptions = {
  title?: string;
  icon?: IconName;
  isHidden?: boolean;
};

// Map of event name and the type of data (in event.data)
//
// canPreventDefault: true adds the defaultPrevented property to the
// emitted events.
type TabNavigationEventMap = {
  tabPress: {
    data: { isAlreadyFocused: boolean };
    canPreventDefault: true;
  };
};

// The props accepted by the component is a combination of 3 things
type Props = Omit<
  DefaultNavigatorOptions<
    ParamListBase,
    string | undefined,
    TabNavigationState<ParamListBase>,
    TabNavigationOptions,
    TabNavigationEventMap,
    unknown
  >,
  'children'
> &
  TabRouterOptions &
  TabNavigationConfig;

const Screen = styled.View(({ theme }) => {
  const { height } = useWindowDimensions();

  return {
    height,
    backgroundColor: theme.color.base,
  };
});

const TopBar = styled(Row)(({ theme }) => ({
  backgroundColor: theme.color['base-grey'],
  justifyContent: 'space-between',
  paddingHorizontal: PADDING.m,
  height: BASE_SIZE[48],
  maxWidth: '100%',
}));

const Page = styled.View({
  flex: 1,
});

const Content: React.FC<TabNavigationConfig> = ({
  actions = [],
  name = '',
}) => {
  const { state } = Navigator.useContext();
  const currentRouteName = state.routeNames[state.index];

  const session = useUserSession();
  const isAdmin = session.hasAny([GroupSecurity.Admin]);

  const navigateToProfile = React.useCallback(() => {
    router.push('/profile');
  }, []);

  return (
    <Screen>
      <TopBar unsetZIndex>
        <Row unsetZIndex>
          <Logo />

          <Tab
            isActive={currentRouteName === 'index'}
            text="Home"
            icon="home"
            onPress={() => router.push('/')}
          />

          <Tab
            isActive={currentRouteName === 'dashboard'}
            text="Dashboard"
            icon="dashboard"
            onPress={() => router.push('dashboard')}
          />

          <Tab
            isActive={currentRouteName.startsWith('patient')}
            text="Patients"
            icon="people-outline"
            onPress={() => router.push('patient')}
          />

          <Visible if={isAdmin}>
            <Tab
              isActive={currentRouteName === 'admin'}
              text="Admin"
              icon="admin-panel-settings"
              onPress={() => router.push('admin')}
            />
          </Visible>

          {actions.map((action) => (
            <Visible key={action.title} if={!action.isHidden}>
              <Tab
                onPress={action.onPress}
                icon={action.icon}
                text={action.title}
                isActive={false}
              />
            </Visible>
          ))}
        </Row>
        <Row unsetZIndex>
          <Profile
            name={name}
            onProfilePress={navigateToProfile}
            wards={session.wards}
            ward={session.ward}
            onWardSelect={session.selectWard}
          />
        </Row>
      </TopBar>

      <Page>
        <Slot />
      </Page>
    </Screen>
  );
};

/**
 * Web-style Top Navigator implemented as per the
 * [React-Navigation Documentation Sample](https://reactnavigation.org/docs/custom-navigators/)
 */
export const TopNavigator = ({
  initialRouteName,
  screenOptions,
  ...rest
}: Props): JSX.Element => {
  // note that in the case of small devices the header will overflow and not scroll. this is
  // necessary to ensure the Profile dropdown works correctly. in such cases be sure to show the
  // a different mobile navigation or reduce the number of options available here
  return (
    <Navigator
      router={TabRouter}
      initialRouteName={initialRouteName}
      screenOptions={screenOptions}
    >
      <Content {...rest} />
    </Navigator>
  );
};
